import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store";
import {
  getPlayerRiskRestrictions,
  RestrictionRow,
} from "store/players/list/slice";

import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import SwitchBtn from "components/buttons/SwitchBtn";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { formatDateTime, useTimeFormat } from "helpers/timeFormat";

const restrictionLabels: Record<string, string> = {
  user: "User",
  deposit: "Deposit",
  withdraw: "Withdraw",
  casino: "Casino",
  sport: "Sport",
  poker: "Poker",
  bonus: "Bonus",
  cashback: "Cashback",
  promoCode: "Promo code",
  tournament: "Tournament",
  transfer: "Transfer",
  cryptoConvert: "Crypto convert",
  affiliate: "Affiliate",
};

const restrictionOrder = [
  "user",
  "deposit",
  "withdraw",
  "casino",
  "sport",
  "poker",
  "bonus",
  "cashback",
  "promoCode",
  "tournament",
  "transfer",
  "cryptoConvert",
  "affiliate",
];

const restrictionIcons: Record<string, string> = {
  deposit: "/sidebar/deposits.svg",
  withdraw: "/sidebar/withdrawals.svg",
  casino: "/sidebar/casino.svg",
  sport: "/sidebar/sport.svg",
  poker: "/sidebar/poker.svg",
  bonus: "/sidebar/bonuses.svg",
  cashback: "/sidebar/cashbacks.svg",
  promoCode: "/sidebar/promocodes.svg",
  tournament: "/sidebar/tournaments.svg",
  transfer: "/sidebar/transfer.svg",
  cryptoConvert: "/sidebar/currency.svg",
  affiliate: "/sidebar/affiliates.svg",
  user: "/sidebar/player.svg",
};

interface Props {
  rows: RestrictionRow[];
  setRows: React.Dispatch<React.SetStateAction<RestrictionRow[]>>;
  setInitialRows: React.Dispatch<React.SetStateAction<RestrictionRow[]>>;
  showRestrictionErrors: boolean;
}

const ListRestrictionsTab: React.FC<Props> = ({
  rows,
  setRows,
  showRestrictionErrors,
  setInitialRows,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const timeFormat = useTimeFormat();
  const entityId = searchParams.get("entityId");
  const riskRestrictions = useAppSelector(
    (store) => store.players.riskRestrictions,
  );
  const isRiskRestrictionsFetched = useAppSelector(
    (store) => store.players.isRiskRestrictionsFetched,
  );
  const permissions = usePermissionHelper();
  const canUpdate = permissions?.update ?? false;

  useEffect(() => {
    if (entityId) {
      dispatch(getPlayerRiskRestrictions(entityId));
    }
  }, [dispatch, entityId]);

  const mappedRows = useMemo(() => {
    return restrictionOrder.map((type) => {
      const apiItem = riskRestrictions?.items?.find(
        (item) => item.restrictionType === type,
      );

      return {
        restrictionType: type,
        restriction: restrictionLabels[type] ?? type,
        isBlocked: apiItem?.isBlocked ?? false,
        reason: apiItem?.reason ?? "",
        source: apiItem?.source ?? "—",
        updatedAt: apiItem?.updatedAt
          ? formatDateTime(apiItem.updatedAt, timeFormat)
          : "—",
      };
    });
  }, [riskRestrictions, timeFormat]);

  useEffect(() => {
    setRows(mappedRows);
    setInitialRows(mappedRows);
  }, [mappedRows]);

  const handleToggle = (restrictionType: string) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.restrictionType !== restrictionType) {
          return row;
        }

        const nextBlocked = !row.isBlocked;

        return {
          ...row,
          isBlocked: nextBlocked,
          reason: nextBlocked ? row.reason : "",
        };
      }),
    );

    setTimeout(() => {
      const input = document.querySelector(
        `#${restrictionType}`,
      ) as HTMLInputElement | null;

      input?.focus();
    }, 0);
  };

  const handleReasonChange = (restrictionType: string, value: string) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.restrictionType !== restrictionType) {
          return row;
        }

        return {
          ...row,
          reason: value.slice(0, 250),
        };
      }),
    );
  };

  if (!isRiskRestrictionsFetched) {
    return <ModalTabSkeleton />;
  }

  return (
    <div className="risk-restrictions-content">
      <div className="inner-table-wrapper risk-restrictions-table">
        <table>
          <thead>
            <tr>
              <th>Restriction</th>
              <th>Status</th>
              <th className="reason-column">Reason</th>
              <th>Source</th>
              <th>Updated at</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr key={row.restrictionType}>
                <td>
                  <div className="restriction-cell">
                    <img
                      src={restrictionIcons[row.restrictionType]}
                      alt={row.restriction}
                    />

                    <span>{row.restriction}</span>
                  </div>
                </td>

                <td>
                  <div className="restriction-status-cell">
                    <SwitchBtn
                      isActive={row.isBlocked}
                      handleClick={() => handleToggle(row.restrictionType)}
                      isRed
                      disabled={!canUpdate}
                    />

                    <span
                      className={`badge ${
                        row.isBlocked ? "badge--blocked" : "badge--low"
                      }`}
                    >
                      {row.isBlocked ? "Blocked" : "Allowed"}
                    </span>
                  </div>
                </td>

                <td className="restriction-reason-cell">
                  {row.isBlocked ? (
                    <InnerInput
                      id={row.restrictionType}
                      type="text"
                      value={row.reason}
                      onChange={(value) =>
                        handleReasonChange(row.restrictionType, value)
                      }
                      title={row.reason}
                      disabled={!canUpdate}
                      hasError={
                        showRestrictionErrors &&
                        row.isBlocked &&
                        !row.reason.trim()
                      }
                    />
                  ) : (
                    "—"
                  )}
                </td>

                <td>{row.source}</td>

                <td>{row.updatedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ListRestrictionsTab;
